import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PageBreadcrumbs from "@ucs/Overrides/Components/PageBreadcrumbs";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface OrderDetailsContainerStyles {
    container: InjectableCss;
    orderTitleOrderActions: InjectableCss;
    contentContainer?: InjectableCss;
    informationContainer?: InjectableCss;
    cartTotalContainer?: InjectableCss;
    addressesSection?: InjectableCss;
}

export const orderDetailsContainerStyles: OrderDetailsContainerStyles = {
    container: {
        css: css`
            max-width: 1400px;
            margin: 0 auto;
            padding: 32px 0 96px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        padding: 16px 20px 32px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 16px 20px 32px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 24px 20px 48px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 24px 20px 48px;
                    `,
                ]);
            }}
        `,
    },
    orderTitleOrderActions: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 30px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            gap: 0px;
                            margin-bottom: 24px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    contentContainer: {
        css: css`
            width: 100%;
            display: flex;
            gap: 30px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        gap: 0;
                    `,
                    css`
                        flex-direction: column;
                        gap: 0;
                    `,
                ]);
            }}
        `,
    },
    informationContainer: {
        css: css`
            flex: 1;
            & > * {
                height: auto !important;
            }
        `,
    },
    cartTotalContainer: {
        css: css`
            max-width: 350px;
            margin-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                ]);
            }}
        `,
    },
    addressesSection: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, calc(50% - 5px));
            gap: 10px;
            /* align-items: stretch; */
            & > div {
                margin-bottom: 10px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            grid-template-columns: 100%;
                            gap: 0;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
};

const OrderDetailsContainer: FC<WidgetProps> = ({ id }) => {
    const styles = orderDetailsContainerStyles;

    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {/* UFG-270 | Remove breadcrumb from Order Details heading widget */}
            {/* <PageBreadcrumbs /> */}
            {/* UFG-270 */}
            <StyledWrapper {...styles.orderTitleOrderActions}>
                <Zone contentId={id} zoneName="Content00" />
            </StyledWrapper>

            <StyledWrapper {...styles.contentContainer}>
                <StyledWrapper {...styles.informationContainer}>
                    <Zone zoneName="Content01" contentId={id} />
                    <StyledWrapper {...styles.addressesSection}>
                        <Zone contentId={id} zoneName="ShippingAddress" />
                        <Zone contentId={id} zoneName="BillingAddress" />
                    </StyledWrapper>
                    <Zone zoneName="Content03" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.cartTotalContainer}>
                    <Zone zoneName="Content02" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper>
                <Zone contentId={id} zoneName="OrderedItems" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const orderDetailsContainer: WidgetModule = {
    component: OrderDetailsContainer,
    definition: {
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default orderDetailsContainer;
